import { useEffect, useState } from 'react';
import { UTM_KEYS } from '../lib/signupAttribution';
import { navegadorEmbebido, type NavegadorEmbebido } from '../lib/navegadorEmbebido';
import { intentParaChrome, urlConAtribucion, type AtribucionGuardada } from '../lib/googleEnNavegadorEmbebido';
import { trackEvent } from '../lib/posthog';

// Aviso para quien abrió Atlas dentro de Instagram, Facebook y compañía, al lado de los CUATRO
// botones de Google de la app: el registro (`AuthForm`), la auth vieja (`AuthCompat`), el alta
// por link que cobra (`EnterpriseCheckout`) y Tiendanube. Un test verifica que ningún botón de
// Google quede afuera. Ver ADR-2026-10-20-google-avisa-en-instagram-sin-frenar.
//
// NO FRENA EL BOTÓN. Google deja entrar a más o menos la mitad de los que lo intentan desde
// Instagram (35 usuarios en 30 días entraron así), así que interceptarlos a todos le agregaba
// un paso a quien hoy entra bien. El aviso está a la vista desde que se abre la pantalla: al
// que Google no deja, ya le dijimos qué hacer antes de que tocara.
//
// Los colores y tamaños son los de `AvisoCuentaNueva`, el aviso que ya vive al lado del botón
// de Google en el registro: así se ve de la casa en las cuatro pantallas.

/** Lo que la llegada dejó guardado (`captureAttributionParams`), para que viaje en el link. */
function leerAtribucionGuardada(): AtribucionGuardada {
  const guardado: AtribucionGuardada = {};
  try {
    for (const key of UTM_KEYS) guardado[key] = localStorage.getItem(`atlas:${key}`);
    guardado.ref = localStorage.getItem('atlas:ref');
    guardado.fbc = localStorage.getItem('atlas:fbc');
  } catch {
    // Sin storage no hay nada que sumar: el link va con lo que ya tenga la URL.
  }
  return guardado;
}

/**
 * La URL de esta pantalla con la atribución pegada, y la deja puesta en la barra.
 *
 * El "⋯ → Abrir en el navegador" de Instagram abre la URL QUE ESTÁ EN LA BARRA, y Chrome o
 * Safari arrancan con el storage vacío: si la atribución no está ahí, el navegador nuevo no
 * sabe de qué anuncio vino. Se usa `replaceState` y no `navigate` a propósito: no re-renderiza
 * la pantalla ni dispara sus efectos (el alta por link lee su query en varios lugares). Se
 * conserva el `state` del historial, que es de React Router.
 */
function urlParaAbrirAfuera(): string {
  const url = urlConAtribucion(window.location.href, leerAtribucionGuardada());
  if (url !== window.location.href) {
    try {
      window.history.replaceState(window.history.state, '', url);
    } catch {
      // Si no se puede reescribir, el botón de Chrome igual usa la URL armada.
    }
  }
  return url;
}

/**
 * El hook de cada pantalla con botón de Google. Fuera de un navegador embebido devuelve
 * `embebido: null` y no hace nada. Dentro, al montar, deja la atribución en la URL (para el
 * "Abrir en el navegador" del menú de la app) y registra que el aviso se vio.
 */
export function useAvisoGoogleEnApp(pantalla: string) {
  const [embebido] = useState<NavegadorEmbebido | null>(() =>
    typeof navigator === 'undefined' ? null : navegadorEmbebido(navigator.userAgent),
  );
  useEffect(() => {
    if (!embebido) return;
    urlParaAbrirAfuera();
    trackEvent('google_aviso_en_app_visto', { app: embebido.app ?? 'webview', android: embebido.android, pantalla });
  }, [embebido, pantalla]);
  return { embebido };
}

/**
 * Corto a propósito (pedido de Imanol): una pregunta y UNA salida, más el correo.
 * - Android: un botón que abre Chrome directo (`intent://`). Instagram no garantiza
 *   respetarlo; si en la prueba real no abre, pasa a la instrucción del menú como en iPhone.
 * - iPhone: Instagram no deja abrir Safari desde una página, así que no hay botón posible;
 *   la instrucción dice DÓNDE está el menú.
 * La pregunta se queda: a la mitad Google sí la deja entrar, y "no funciona" sería falso.
 */
export function AvisoGoogleEnApp({ embebido, pantalla }: { embebido: NavegadorEmbebido; pantalla: string }) {
  const alAbrirEnChrome = () => {
    trackEvent('google_aviso_en_app_eleccion', { opcion: 'abrir_chrome', app: embebido.app ?? 'webview', pantalla });
    const intent = intentParaChrome(urlParaAbrirAfuera());
    if (intent) window.location.href = intent;
  };

  return (
    <div className="flex w-full flex-col gap-2.5 rounded-[12px] bg-[#DBEAFE] px-4 py-3">
      <p className="text-[13px] font-[700] leading-[18px] text-[#1E40AF]">¿Google no te deja entrar?</p>
      {embebido.android ? (
        <button
          type="button"
          onClick={alAbrirEnChrome}
          className="w-full rounded-[10px] bg-[#1E40AF] px-4 py-2.5 text-[14px] font-[700] leading-[18px] text-white"
        >
          Abrir en Chrome
        </button>
      ) : (
        <p className="text-[13px] font-[500] leading-[18px] text-[#1E40AF]">
          Tocá <b>⋯</b> arriba a la derecha y elegí <b>«Abrir en el navegador»</b>.
        </p>
      )}
      <p className="text-[12px] font-[500] leading-[17px] text-[#1E40AF]/80">o seguí con tu correo acá abajo</p>
    </div>
  );
}
